import { EXTERNAL_SYNC_PROVIDERS } from '../constants/external-sync.constants';

/**
 * Calculate the numbering for each item in the tree
 * @param {{index: number, level: number}[]} items
 * @returns {string[]} the numbering for each item in the tree
 */
function numberingForHeading(items) {
  const createTree = items => {
    const root = { index: 0, level: 0, numbering: '', parent: null };
    const tree = [root, ...items];

    tree.forEach((item, index) => {
      if (index === 0) return; // Skip root node
      const parent = tree
        .slice(0, index)
        .reverse()
        .find(i => i.level < item.level);

      item.parent = parent.index;
    });

    return tree;
  };

  const numbering = (item, j) => {
    if (item.parent === null) return;
    if (item.parent === 0) item.numbering = `${j + 1}`;
    else {
      const parent = tree.find(i => i.index === item.parent);
      item.numbering = `${parent.numbering}.${j + 1}`;
    }
  };

  // Goes over the tree in DFS to calculate the numbering
  const dfs = node => {
    tree
      .filter(item => item.parent === node.index)
      .forEach((item, i) => {
        numbering(item, i);
        dfs(item);
      });
  };

  const tree = createTree(items);
  dfs(tree[0]); // start from the root
  tree.shift(); // remove the root
  return tree.map(item => item.numbering);
}

const EXTRACTABLE_EXTENSIONS = new Set(['.pdf', '.doc', '.docx', '.ppt', '.pptx']);

const EXTRACTABLE_KNOWLEDGE_TYPES = new Set(['pdf', 'document', 'content']);

/**
 * Check if a file supports metadata extraction based on its extension.
 * Works with upload file objects that have fileName or c8Path properties.
 */
function isMetadataExtractableByExtension(file) {
  const name = file.fileName || file.c8Path || '';
  const dotIndex = name.lastIndexOf('.');
  if (dotIndex === -1) return false;
  const ext = name.substring(dotIndex).toLowerCase();
  return EXTRACTABLE_EXTENSIONS.has(ext);
}

/**
 * Check if a knowledge item supports metadata extraction based on its type.
 */
function isMetadataExtractableByType(type) {
  return EXTRACTABLE_KNOWLEDGE_TYPES.has(type);
}

/**
 * Content synced from an external provider (SharePoint, PolicyTech, …) is a one-way
 * mirror of a system-of-record, so it is read-only in the client — except Labels
 * (org locations) and Share to Hub, which are C8-side classification. Gate on the
 * presence of the provider so any future provider is covered automatically.
 */
function isExternalSyncReadOnly(options) {
  return !!options?.externalSyncProvider;
}

/**
 * PolicyTech mirrors the whole workspace structure, so *creating* sections/binders/
 * directories is blocked for it specifically. SharePoint only syncs items into a
 * binder and leaves the surrounding structure user-managed, so it doesn't block
 * category creation.
 */
function isPolicyTechSynced(options) {
  return options?.externalSyncProvider === EXTERNAL_SYNC_PROVIDERS.POLICYTECH;
}

/**
 * The update payload allowed on externally-synced content: the C8-side classification
 * fields the provider doesn't own — labels (sites), Share to Hub, widget visibility,
 * contributors, the thumbnail and the tag fields. The provider keeps title, subtitle,
 * keywords, body and the file itself; the backend enforces those from the stored
 * document and merges `options`/`tags` per key, so the sync flag survives.
 *
 * Every key is omitted unless explicitly set, so a partial save (e.g. Share to Hub
 * alone) can't wipe a field the user never touched.
 */
function buildSyncedUpdatePayload({
  id,
  labels,
  shareToHub,
  hideInWidget,
  contributors,
  thumbnail,
  specialty,
  subspecialty,
  contentType,
  targetAudience,
  isSendNotification,
  notifyOnWorkingHours,
  recipientsTags,
}) {
  const payload = { id };

  if (labels !== undefined) payload.labels = labels;
  if (shareToHub !== undefined) payload.shareToHub = shareToHub;
  if (hideInWidget !== undefined) payload.options = { hideInWidget };
  if (contributors !== undefined) payload.contributors = contributors;
  if (thumbnail !== undefined) payload.thumbnail = thumbnail;
  // Notifying the team is an action on the edit, not a mirrored field. Callers pass these
  // only when the toggle is actually on, so an ordinary save stays a minimal body.
  if (isSendNotification !== undefined) payload.isSendNotification = isSendNotification;
  if (notifyOnWorkingHours !== undefined) payload.notifyOnWorkingHours = notifyOnWorkingHours;
  if (recipientsTags !== undefined) payload.recipientsTags = recipientsTags;

  // Nested exactly like the normal save; the backend merges per sub-key.
  const tags = {};
  if (specialty !== undefined) tags.specialty = specialty;
  if (subspecialty !== undefined) tags.subspecialty = subspecialty;
  if (contentType !== undefined) tags.contentType = contentType;
  if (targetAudience !== undefined) tags.targetAudience = targetAudience;
  if (Object.keys(tags).length > 0) payload.tags = tags;

  return payload;
}

/**
 * A knowledge item or category — anything carrying the sync flag under `options`.
 * @typedef {{ options?: { externalSyncProvider?: string | null } | null } | null | undefined} VisibilityEntity
 */

/**
 * The field names `shouldShowField` knows about. Call sites are untyped .js (checkJs is
 * off), so a bare string literal typo would silently fail open — import these instead.
 */
const KNOWLEDGE_VISIBILITY_FIELDS = /** @type {const} */ ({
  OWNERS: 'owners',
});

/**
 * Per-field visibility predicates, evaluated against a knowledge item (or its parent
 * category). Only PolicyTech runs the vetting, so only its items drop the C8 vetter.
 * (The review date is always rendered — synced content displays it as "never expires" —
 * so it carries no predicate.)
 */
const FIELD_VISIBILITY_PREDICATES = /** @type {const} */ ({
  owners: (/** @type {VisibilityEntity} */ item) => !isPolicyTechSynced(item?.options),
});

/**
 * The fields `shouldShowField` knows about — derived from the predicate map.
 * @typedef {keyof typeof FIELD_VISIBILITY_PREDICATES} VisibilityField
 */

/**
 * Builds `shouldShowField(field)` for a knowledge item. The parent category can be passed
 * alongside because the synced flag may sit on either: a field is shown only when its
 * predicate passes for every entity given.
 * @param {...VisibilityEntity} entities
 * @returns {(field: VisibilityField) => boolean}
 */
function getFieldVisibility(...entities) {
  return field => {
    const predicate = FIELD_VISIBILITY_PREDICATES[field];
    // Untyped (.js) callers can still pass an unknown field — show it rather than crash.
    return !predicate || entities.every(predicate);
  };
}

/**
 * The review date only travels when the user touched the picker. Untouched → omitted, so the
 * backend keeps the stored value (or, on create, derives the default from the content type);
 * cleared → null, which the backend stores as "never expires".
 * @param {{ expiresAt?: string | null, touched: boolean }} params
 * @returns {{ expiresAt: string | null } | {}} the key is omitted entirely when untouched
 */
function buildExpiresAtPayload({ expiresAt, touched }) {
  if (!touched) return {};
  return { expiresAt: expiresAt || null };
}

export {
  numberingForHeading,
  EXTRACTABLE_EXTENSIONS,
  EXTRACTABLE_KNOWLEDGE_TYPES,
  isMetadataExtractableByExtension,
  isMetadataExtractableByType,
  isExternalSyncReadOnly,
  isPolicyTechSynced,
  buildSyncedUpdatePayload,
  buildExpiresAtPayload,
  getFieldVisibility,
  KNOWLEDGE_VISIBILITY_FIELDS,
};
